<template>
  <div class="new-event">
    <div v-if="isLoadingWebEvent">
      <loadingBlock class="mx-auto">
      </loadingBlock>
    </div>
    <div v-else>
      <transition name="slide-down">
        <div v-if="showStickyBar && navigatorList.length > 1" class="sticky-coupon-bar" :style="{ backgroundColor: '#000000' }">
          <div class="sticky-coupon-bar__content">
            <div class="sticky-coupon-bar__nav" ref="stickyNav">
              <button
                v-for="item in navigatorList"
                :key="`sticky-nav-${item.id}`"
                class="sticky-coupon-bar__nav-item"
                :class="{ 'is-active': activeBlockId === item.id }"
                @click="scrollToBlock(item)"
              >
                {{ item.tab }}
              </button>
            </div>
          </div>
        </div>
      </transition>
      <section class="section-hero" id="section-hero">
        <div class="section-hero__img flex items-center justify-center align-middle" data-aos="zoom-custom" data-aos-offset="500" data-aos-once="true">
          <img :src="coverImage ? `${coverImage}?height=720&format=webp` : require('@/assets/images/default-event.jpg')" alt="">
        </div>
      </section>
      <section class="section-article" id="section-article">
        <div class="section-article__content">
          <h1 class="text-2xl font-medium">{{ event.title }}</h1>
          <h2 class="desp mt-4 text-base text-grey-darker" style="line-height: 1.5;">
            {{ event.subject }}
          </h2>
          <div class="ql-snow mt-48 text-base" style="line-height: 1.5;">
            <div class="ql-editor leading-md whitespace-pre-wrap" style="padding: 0;" v-html="event.description"></div>
          </div>
        </div>
      </section>
      <section class="section-coupon" v-if="event.entities.coupons.length">
        <div class="section-coupon__content">
          <Coupons :coupons="event.entities.coupons" />
        </div>
      </section>
      <section class="section-recommend" v-if="event.blocks.length" ref="sectionRecommend">
        <div class="section-recommend__content">
          <div v-for="block in event.blocks" :key="`eventBlock-${block.id}`" class="recommend-child">
            <div v-if="block.style === 'heading'" :id="block.anchor">
              <h3 class="text-2xl font-medium">{{ block.title }}</h3>
              <div class="text-grey-darker mt-16">{{ block.subject }}</div>
            </div>
            <div v-if="block.style === 'special'" :id="block.anchor">
              <RecommendSwiper :block="block" :event="event" />
            </div>
            <div v-if="block.style === 'tag'" :id="block.anchor">
              <HotelsBlock :hotels="block.items" :list="event.entities.hotels" :title="block.title" />
            </div>
          </div>
        </div>
      </section>
      <section class="section-events">
        <div class="section-events__content">
          <eventSwiper :list="others" />
        </div>
      </section>
      <section v-if="isTraditionalChinese" class="section-blog">
        <div class="section-blog__content">
          <blog :meta="{title: '精選旅遊攻略', description: '奧丁丁秉持玩就對了的精神，一起從當地人的角度發掘每個城市的文化及特色！'}" :data="blog"></blog>
        </div>
      </section>
      <section class="section-notice" v-if="event.instruction">
        <div class="section-notice__content">
          <h3 class="text-2xl font-medium">{{ $t('newEvent.notice.title') }}</h3>
          <div class="mt-24 text-sm md:text-base" style="line-height: 28px;" v-html="event.instruction" />
        </div>
      </section>
      <section v-if="event.entities.faqs.length" class="section-faqs">
        <div class="section-faqs__content">
          <h3 class="text-2xl font-medium mb-24">{{ $t('commonProblem') }}</h3>
          <details class="faq-item" v-for="(item, index) in event.entities.faqs" :key="`faq-${index}`">
            <summary>Q : {{ item.question }}</summary>
            <div class="faq-answer">
              <p>{{ item.answer }}</p>
            </div>
          </details>
        </div>
      </section>
    </div>
  </div>
</template>
<script>
import _ from 'lodash'
import { mapActions, mapGetters, mapMutations } from 'vuex'
import Coupons from '@/components/newEvent/coupons'
import HotelsBlock from '@/components/newEvent/hotelsBlock'
import EventSwiper from '@/components/newEvent/eventSwiper'
import blog from '@/components/newEvent/blog'
import loadingBlock from '@/components/loadingBlock'
import localeMixin from '@/mixins/locale'
import RecommendSwiper from '@/components/newEvent/recommendSwiper'

import animateScrollTo from 'animated-scroll-to'

import 'quill/dist/quill.core.css'
import 'quill/dist/quill.snow.css'

export default {
  name: 'NewEvent',
  metaInfo () {
    return {
      title: this.event.title,
      meta: [
        {
          'name': 'description',
          'content': this.event.htmlmetas?.description || this.event.description.replace(/<[^>]*>?/gm, ''),
          'vmid': 'description'
        },
        {
          'property': 'og:title',
          'content': this.event.title,
          'vmid': 'og:title'
        },
        {
          'property': 'og:description',
          'content': this.event.intro || this.event.description.replace(/<[^>]*>?/gm, ''),
          'vmid': 'og:description'
        },
        {
          'property': 'og:image',
          'content': this.coverImages,
          'vmid': 'og:image'
        }
      ],
      script: [
        {
          type: 'application/ld+json',
          json: {
            '@context': 'https://schema.org',
            '@type': 'ItemList',
            name: this.event.title,
            description: this.event.description.replace(/<[^>]*>?/gm, ''),
            'itemListElement': this.eventHotelList()
          }
        },
        {
          type: 'application/ld+json',
          json: {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: this.eventFAQList()
          }
        }
      ]
    }
  },
  components: {
    Coupons,
    EventSwiper,
    blog,
    HotelsBlock,
    loadingBlock,
    RecommendSwiper
  },
  data () {
    return {
      isTop: false,
      isLoadingWebEvent: true,
      recommendVisible: false,
      activeBlockId: null,
      recommendObserver: null,
      blockObserver: null,
      throttledScrollHandler: null,
      event: {
        description: '',
        images: [''],
        entities: {},
      },
      blog: [],
      others: [],
      blockMarginLeft: 0,
      optionsRecommend: {
        loop: false,
        preventInteractionOnTransition: true,
        slideToClickedSlide: false,
        breakpoints: {
          3000: {
            slidesPerView: 4,
            slidesPerGroup: 4,
            spaceBetween: 16
          },
          2000: {
            slidesPerView: 3,
            slidesPerGroup: 3,
            spaceBetween: 16
          },
          1300: {
            slidesPerView: 2,
            slidesPerGroup: 2,
            spaceBetween: 16
          },
          1024: {
            slidesPerView: 1.5,
            slidesPerGroup: 1.5,
            spaceBetween: 16
          },
          430: {
            slidesPerView: 1.1,
            slidesPerGroup: 1.1,
            spaceBetween: 16
          }
        }
      }
    }
  },
  mixins: [localeMixin],
  computed: {
    ...mapGetters({
      isDesktop: 'isDesktop',
      isMobile: 'isMobile',
      selectedLang: 'user/selectedLang',
      currencySign: 'user/currencySign',
      currency: 'user/selectedCurrency'
    }),
    showStickyBar () {
      return this.recommendVisible
    },
    navigatorList: function () {
      return this.event?.blocks.reduce((acc, curr) => {
        if (curr.style !== 'heading') {
          acc.push({
            id: curr.id,
            anchor: curr.anchor,
            title: curr.title,
            tab: curr.tab_title
          })
        }
        return acc
      }, [])
    },
    coverImage: function () {
      if (!this.event.coverImages || !this.event.coverImages.length) {
        return false
      }
      if (this.isMobile) {
        return this.event.coverImages.find(e => e.role === 'mobile')?.url || this.event.coverImages.find(e => e.role === 'desktop')?.url
      } else {
        return this.event.coverImages.find(e => e.role === 'desktop')?.url
      }
    }
  },
  methods: {
    ...mapMutations([
      'events/SET_HEADERTEXTCOLOR',
      'events/SET_HEADERBGCOLOR'
    ]),
    ...mapActions([
      'events/fetchWebEvent'
    ]),
    eventFAQList: function () {
      return this.event?.entities?.faqs?.reduce((acc, curr) => {
        acc.push({
          '@type': 'Question',
          name: curr.question,
          acceptedAnswer: {
            '@type': 'Answer',
            'text': curr.answer
          }
        })
        return acc
      }, [])
    },
    eventHotelList: function () {
      const hotels = this.event.blocks?.flatMap(block => block.items || []) || []
      return hotels.reduce((acc, hotel, index) => {
        const details = this.event.entities.hotels?.[hotel.hotelUUID]
        if (!details) return acc
        acc.push({
          '@type': 'ListItem',
          position: index + 1,
          url: `https://journey.owlting.com/${hotel.hotelUUID}`,
          item: {
            '@type': 'LodgingBusiness',
            name: details.hotelNameLocal,
            address: {
              '@type': 'PostalAddress',
              addressLocality: details.placesText
            },
            priceRange: hotel.price ? `${hotel.currency} ${hotel.price}+` : undefined
          }
        })
        return acc
      }, [])
    },
    fetchWebEvent: function () {
      this.isLoadingWebEvent = true
      this['events/fetchWebEvent'](this.$route.params.eventId)
        .then(res => {
          this.event = res.event
          this.others = res.others
          this.blog = res.articles
          this.isLoadingWebEvent = false
        })
        .finally(() => {
          // document.getElementsByClassName('travelNav')[0].style.backgroundColor = '#E0B296'
          document.getElementsByClassName('section-hero')[0].style.backgroundColor = this.event.bgColor
          this['events/SET_HEADERTEXTCOLOR'](this.event.headerTextColor)
          this['events/SET_HEADERBGCOLOR'](this.event.bgColor)
          const hash = this.$route.hash
          if (hash) {
            setTimeout(() => {
              const el = document.getElementById(hash.slice(1))
              if (el) animateScrollTo(el, { offset: this.isDesktop ? -125 : -109 })
            }, 200)
          } else if (this.$route.query?.id) {
            setTimeout(() => {
              animateScrollTo(document.getElementById(`newEvent-anchor-${this.$route.query?.id}`), {
                offset: this.isDesktop ? -125 : -109
              })
            }, 200)
          }
        })
    },
    reFetchWebEvent: function () {
      this['events/fetchWebEvent'](this.$route.params.eventId)
        .then(res => {
          this.event = res.event
          this.others = res.others
          this.blog = res.articles
        }).finally(() => {
          // animateScrollTo(document.getElementsByClassName('event__items')[0], {
          //   offset: this.isDesktop ? -125 : -109
          // })
        })
    },
    initCouponObserver () {
      if (this.recommendObserver) this.recommendObserver.disconnect()

      if (this.$refs.sectionRecommend) {
        this.recommendObserver = new IntersectionObserver(
          ([entry]) => {
            this.recommendVisible = entry.isIntersecting
          },
          { rootMargin: '0px 0px -90% 0px', threshold: 0 }
        )
        this.recommendObserver.observe(this.$refs.sectionRecommend)
      }

      if (this.blockObserver) this.blockObserver.disconnect()
      this.blockObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              const block = this.navigatorList.find(b => b.anchor === entry.target.id)
              if (block) {
                this.activeBlockId = block.id
                // history.replaceState(null, '', `#${block.anchor}`)
              }
            }
          })
        },
        { rootMargin: '-60px 0px -75% 0px', threshold: 0 }
      )
      this.navigatorList.forEach(item => {
        const el = document.getElementById(item.anchor)
        if (el) this.blockObserver.observe(el)
      })
    },
    scrollToBlock (item) {
      const el = document.getElementById(item.anchor)
      if (!el) return
      this.activeBlockId = item.id
      animateScrollTo(el, { offset: this.isDesktop ? -125 : -109 })
      if (item.anchor) {
        history.replaceState(null, '', `#${item.anchor}`)
      }
    },
    checkArticleToTop () {
      const el = document.getElementById('section-article')
      if (!el) return
      this.isTop = el.getBoundingClientRect().top >= 60
    },
    goToHotel: function (hotel) {
      return {
        name: 'HotelDetail',
        params: { hotelId: hotel.hotelUUID },
        query: {
          externalId: hotel.hotelUUID,
          searchId: hotel.hotelUUID,
          searchType: 'hotel',
          source: this.event.entities.hotels[hotel.hotelUUID].source
        }
      }
    }
  },
  created () {
    this.fetchWebEvent()
  },
  mounted () {
  },
  beforeDestroy () {
    if (this.recommendObserver) this.recommendObserver.disconnect()
    if (this.blockObserver) this.blockObserver.disconnect()
    if (this.throttledScrollHandler) window.removeEventListener('scroll', this.throttledScrollHandler)
  },
  watch: {
    isTop: {
      handler (newVal) {
        if (!newVal) {
          this['events/SET_HEADERBGCOLOR']('#ffffff')
          this['events/SET_HEADERTEXTCOLOR']('#000000')
        } else {
          this['events/SET_HEADERBGCOLOR'](this.event.bgColor)
          this['events/SET_HEADERTEXTCOLOR'](this.event.headerTextColor)
        }
      },
      immediate: false
    },
    activeBlockId () {
      this.$nextTick(() => {
        const activeEl = this.$refs.stickyNav?.querySelector('.is-active')
        if (activeEl) activeEl.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' })
      })
    },
    blockMarginLeft (newVal) {
      document.getElementsByClassName('section-recommend__content')[0].style.marginLeft = newVal + 'px'
    },
    isLoadingWebEvent (newVal) {
      if (!newVal) {
        setTimeout(() => {
          this.checkArticleToTop()
          this.throttledScrollHandler = _.throttle(this.checkArticleToTop, 100)
          window.addEventListener('scroll', this.throttledScrollHandler)
          this.$nextTick(() => {
            this.initCouponObserver()
          })
        }, 100)
      }
    },
    '$route': function (to, from) {
      if (to.path !== from.path) {
        this.recommendVisible = false
        this.activeBlockId = null
        if (this.recommendObserver) this.recommendObserver.disconnect()
        if (this.blockObserver) this.blockObserver.disconnect()
        this.fetchWebEvent()
      } else if (to.hash !== from.hash && to.hash) {
        const el = document.getElementById(to.hash.slice(1))
        if (el) animateScrollTo(el, { offset: this.isDesktop ? -125 : -109 })
      }
    },
    currency: function (to, from) {
      if (to !== from) {
        this.reFetchWebEvent()
      }
    }
  }
}
</script>
<style lang="sass" scoped>
@import '@/assets/style/_mixins.sass'
.new-event
  @apply w-full
  &__nav
    @apply fixed flex items-center justify-between px-16 py-12 text-white font-bold
    z-index: 1
    height: 56px
    mix-blend-mode: difference
    .logo-section
      @apply text-3xl
    .function-section
      @apply text-sm
      & > ul
        @apply list-reset flex items-stretch h-full
        & > li
          @apply px-16 flex-auto flex content-center
          a
            @apply h-full flex content-center items-center
        & > li:not(.user-status)
          @apply hidden
          @screen lg
            @apply flex items-center
      .nav-dropdown
        @apply relative flex items-stretch h-full
        &__display
          @apply flex content-center items-center
          button
            @apply font-bold text-sm
        &__list
          @apply absolute shadow-lg z-10 rounded-b-sm hidden overflow-hidden
          min-width: 150px
          width: 100%
          top: 100%
          right: -16px
          transition: all 0.3s ease
          li .router-link-active span
            @apply text-blue-light
          a
            @apply text-grey-darker
          li
            @apply font-medium leading-normal px-16 py-8 cursor-pointer text-sm w-full
            i
              @apply text-grey-light
              &:before
                @apply mx-0
            &:hover
              @apply bg-grey-lightest
              a
                @apply text-grey-darkest
            &.activate
              @apply font-bold text-blue-light
        &:hover
          & > .nav-dropdown__list
            @apply block
  .sticky-coupon-bar
    @apply fixed w-full
    left: 0
    top: 52px
    @screen lg
      top: 60px
    height: 52px
    z-index: 10
    // box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15)
    &__content
      @apply mx-auto h-full overflow-hidden
      max-width: 1300px
    &__nav
      @apply flex items-end h-full
      overflow-x: auto
      -webkit-overflow-scrolling: touch
      scrollbar-width: none
      &::-webkit-scrollbar
        display: none
      padding: 0 16px
      gap: 4px
      @screen md
        padding: 0
    &__nav-item
      @apply text-base font-medium cursor-pointer
      color: #A3A3A3
      background-color: #171717
      flex-shrink: 0
      border: none
      outline: none
      border-radius: 8px 8px 0 0
      padding: 0 16px
      height: 38px
      white-space: nowrap
      transition: background-color 0.2s ease
      &:hover
        @apply bg-white text-grey-darkest
      &.is-active
        @apply bg-white text-grey-darkest
  .section-hero
    @apply w-full px-16
    @screen md
      @apply px-0
      // padding-top: 60px
    &__img
      @apply mx-auto
      max-width: 1300px
      padding: 60px 0
  .section-article
    @apply w-full bg-white px-16
    @screen md
      @apply px-0
    &__content
      @apply mx-auto
      max-width: 1300px
      padding: 60px 0px
      @screen md
        padding: 100px 0px
  .section-coupon
    @apply w-full bg-black
    &__content
      @apply mx-auto
      max-width: 1300px
      padding: 100px 16px
      @screen md
        padding: 100px 0px
      .swiper-container
        // overflow: visible
        .swiper-slide
          max-width: 500px
  .section-recommend
    @apply w-full bg-white
    &__content
      @apply mx-auto
      max-width: 1300px
      padding: 100px 0px 100px 16px
      @screen md
        padding: 100px 0px
      .swiper-container
        @apply mx-0 w-full
        // overflow: visible
        .swiper-slide
          min-width: 320px
          @screen md
            max-width: 500px
      .special-title
        @apply w-full
        @screen md
          min-width: 224px
          max-width: 224px
      .special-desp
        @include ellipsis(3)
      &__cover
        @apply bg-cover bg-center relative
        transition-duration: .5s
        padding-top: imageRatio(500, 300)
        // &:hover
        //   transform: scale(1.05)
      .recommend-child:not(:last-child)
        margin-bottom: 60px
        @screen md
          margin-bottom: 100px
  .section-hotels
    @apply w-full bg-white
    &__content
      @apply mx-auto
      // max-width: 1300px
      padding: 100px 16px
      @screen md
        padding: 100px 0px
      .swiper-container
        // overflow: visible
        .swiper-slide
          // max-width: 500px
      &__cover
        @apply bg-cover bg-center relative
        padding-top: imageRatio(500, 300)
  .section-events
    @apply w-full bg-black
    &__content
      @apply mx-auto
      max-width: 1300px
      padding: 100px 0px 52px 16px
      @screen md
        padding: 100px 0px 52px 0px
      .swiper-container
        // overflow: visible
        .swiper-slide
          max-width: 400px
      &__cover
        @apply bg-cover bg-center relative
        padding-top: imageRatio(500, 300)
  .section-blog
    @apply w-full bg-grey-lighter
    &__content
      @apply mx-auto
      max-width: 1300px
      padding: 100px 16px
      @screen md
        padding: 100px 0px
  .section-notice
    @apply w-full bg-white px-16
    @screen xl
      @apply px-0
    &__content
      @apply mx-auto
      max-width: 1300px
      padding: 100px 16px
      @screen md
        padding: 100px 0px
      li
        @apply leading-loose
  .section-faqs
    @apply w-full bg-white px-16
    @screen xl
      @apply px-0
    &__content
      @apply mx-auto
      max-width: 1300px
      padding: 100px 16px
      @screen md
        padding: 100px 0px
      li
        @apply leading-loose
</style>
<style lang="sass">
.slide-down-enter-active, .slide-down-leave-active
  transition: transform 0.3s ease

.slide-down-enter, .slide-down-leave-to
  transform: translateY(-100%)

@keyframes custom-zoom
  from
    transform: scale(0.5)
    opacity: 0
  to
    transform: scale(1)
    opacity: 1

@keyframes faqFadeIn
  from
    opacity: 0
    transform: translateY(-5px)
  to
    opacity: 1
    transform: translateY(0)

[data-aos="zoom-custom"]
  animation-name: custom-zoom
  animation-duration: 0.8s
  animation-fill-mode: both

.faq-item
  // border-bottom: 1px solid #eeeeee
  transition: all 0.3s ease
  /* 移除 Chrome, Edge, Firefox 的預設箭頭 */
  summary
    @apply px-0 py-24 flex justify-between items-center cursor-pointer text-base font-bold
    list-style: none

  /* 移除 Safari 的預設箭頭 */
  summary::-webkit-details-marker
    display: none

  summary::after
    @apply text-xl text-grey inline-block ml-16
    content: '+'
    transition: transform 0.3s ease

  &[open] summary::after
    transform: rotate(45deg)

  .faq-answer
    @apply p-0 pb-24 text-base
    animation: faqFadeIn 0.4s ease-out
</style>
